import React, { ReactNode, useContext } from 'react';

import { Box, Tooltip, Typography } from '@material-ui/core';
import { createStyles, alpha, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import ChatAvatar from '@vyce/core/src/components/Chat/ChatAvatar';
import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { CompanyWorkerItem } from '@vyce/core/src/types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      padding: '6px 8px',
      borderRadius: 8,
      cursor: 'pointer',
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shortest,
      }),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    rowSelected: {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
      },
    },
    rowDisabled: {
      cursor: 'default',
      opacity: theme.palette.action.disabledOpacity,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    name: {
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '24px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    subtitle: {
      fontSize: 13,
      lineHeight: '18px',
      color: theme.palette.text.secondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    meta: {
      display: 'flex',
      justifyContent: 'flex-end',
      maxWidth: '45%',
      // Meta is usually a wrapping flex row of chips (ModuleOnboardingChips); when it wraps,
      // its second line must stay right-aligned too, not fall back to the container's start
      '& > *': {
        justifyContent: 'flex-end',
      },
    },
  })
);

interface Props {
  worker: CompanyWorkerItem;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  /** Shown on hover when the row is disabled */
  disabledTooltip?: string;
  /** Extra content between the name and the checkbox, e.g. module chips */
  meta?: ReactNode;
  /** Secondary line under the name, e.g. contact details to tell namesakes apart */
  subtitle?: ReactNode;
  checkboxVariant?: 'circle' | 'rectangle';
}

/** One selectable person in a list: avatar, name, optional meta and a trailing checkbox. */
export const WorkerPickerRow: React.FC<Props> = ({
  worker,
  checked,
  onToggle,
  disabled = false,
  disabledTooltip,
  meta,
  subtitle,
  checkboxVariant = 'circle',
}) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const fullName = `${worker.first_name} ${worker.last_name}`.trim();

  const row = (
    <Box
      display="flex"
      alignItems={isMobile ? 'flex-start' : 'center'}
      gridGap={12}
      className={clsx(classes.row, {
        [classes.rowSelected]: checked && !disabled,
        [classes.rowDisabled]: disabled,
      })}
      onClick={disabled ? undefined : onToggle}
    >
      <ChatAvatar name={fullName} avatarUrl={worker.avatar} size={40} />

      <Box flex={1} minWidth={0}>
        <Typography className={classes.name}>{fullName}</Typography>
        {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
      </Box>

      {meta && <Box className={classes.meta}>{meta}</Box>}

      <AppCheckbox
        checked={checked}
        disabled={disabled}
        variant={checkboxVariant}
        color="primary"
        onClick={event => event.stopPropagation()}
        onChange={onToggle}
      />
    </Box>
  );

  if (disabled && disabledTooltip) {
    return <Tooltip title={disabledTooltip}>{row}</Tooltip>;
  }

  return row;
};
